import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { authApi } from '../../api/auth';
import { errorMessage } from '../../api/client';
import { useTenantList } from '../../api/tenants';
import {
  ACCOUNT_STATE_LABELS,
  displayName,
  ROLE_LABELS,
  type AccountState,
  type TotpEnrolment,
} from '../../api/types';
import { useAuth, useMe } from '../../auth/AuthContext';
import { E164 } from '../../auth/route-state';
import { Alert, Badge, Button, Card, TextField, formatDate, PageHeader } from '../../components/ui';

/**
 * The signed-in user's profile facts and sign-in options. Passwords are set
 * at sign-in and reset from the sign-in page (the reset flow is public, and a
 * signed-in user is sent away from it), so this page only says where to go.
 */
export function AccountSecurity() {
  const me = useMe();
  const isStaff = me.principal.actorKind === 'staff';
  const hasTenant = me.principal.tenantId !== null;
  const tenantQuery = useTenantList({ limit: 1 }, { enabled: !isStaff && hasTenant });
  const organization = isStaff ? (
    'Balsam Solutions'
  ) : !hasTenant ? (
    <span className="muted">no organization yet</span>
  ) : (
    (tenantQuery.data?.data[0]?.name ?? '—')
  );

  return (
    <>
      <PageHeader
        title="Account & security"
        description="Your Balsam Hub profile and sign-in options."
      />
      <div className="grid grid--2">
        <Card title="Profile">
          <dl className="kv">
            <dt>Name</dt>
            <dd>{displayName(me.user)}</dd>
            <dt>Email</dt>
            <dd>
              {me.user.email}{' '}
              <Badge tone={me.user.isEmailVerified ? 'ok' : 'warn'}>
                {me.user.isEmailVerified ? 'verified' : 'unverified'}
              </Badge>
            </dd>
            <dt>Role</dt>
            <dd>
              {ROLE_LABELS[me.principal.role]}{' '}
              <Badge tone={isStaff ? 'accent' : 'neutral'}>{isStaff ? 'staff' : 'tenant'}</Badge>
            </dd>
            <dt>Organization</dt>
            <dd>{organization}</dd>
            <dt>Username</dt>
            <dd>
              <code>{me.user.username}</code>
            </dd>
            <dt>Member since</dt>
            <dd>{formatDate(me.user.createdAt)}</dd>
            <dt>Account</dt>
            <dd>
              <Badge tone={accountStateTone(me.user.accountState)}>
                {ACCOUNT_STATE_LABELS[me.user.accountState]}
              </Badge>
            </dd>
          </dl>
        </Card>

        <Card title="Sign-in">
          <dl className="kv">
            <dt>Password</dt>
            <dd className="muted">
              Set at sign-in. To reset it, sign out and use Forgot password on the sign-in page.
            </dd>
            <dt>Multi-factor</dt>
            <dd>
              <Badge tone={me.user.isMfaEnabled ? 'ok' : 'neutral'}>
                {me.user.isMfaEnabled ? 'enabled' : 'off'}
              </Badge>
            </dd>
            <dt>Phone</dt>
            <dd>
              <strong>{me.user.phone ?? 'not set'}</strong>{' '}
              {me.user.phone ? (
                <Badge tone={me.user.isPhoneVerified ? 'ok' : 'warn'}>
                  {me.user.isPhoneVerified ? 'verified' : 'unverified'}
                </Badge>
              ) : null}
            </dd>
          </dl>
          <MultiFactor />
          <PhoneVerification />
        </Card>

        {me.principal.actorKind === 'tenant' ? <MarketingPreference /> : null}
      </div>
    </>
  );
}

/**
 * MFA the account manages itself: an authenticator app (the secret is shown
 * as text and as an otpauth:// link; no QR code yet), text-message codes when
 * the phone is verified, or off. Each change refreshes /auth/me.
 */
function MultiFactor() {
  const me = useMe();
  const { refreshMe } = useAuth();
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const hasVerifiedPhone = Boolean(me.user.phone) && me.user.isPhoneVerified;

  const start = useMutation({
    mutationFn: async () => {
      setDone(null);
      const started = await authApi.startTotp();
      setEnrolment(started);
      setCode('');
    },
  });

  const confirm = useMutation({
    mutationFn: async () => {
      await authApi.confirmTotp(code.trim());
      await refreshMe().catch(() => null);
      setEnrolment(null);
      setCode('');
      setDone('Your authenticator app is set up. You will be asked for a code at sign-in.');
    },
  });

  const preference = useMutation({
    mutationFn: async (method: 'SMS' | 'NONE') => {
      setDone(null);
      await authApi.setMfaPreference(method);
      await refreshMe().catch(() => null);
      setEnrolment(null);
      setDone(
        method === 'SMS'
          ? 'Sign-in codes will be sent to your phone by text message.'
          : 'Multi-factor authentication is off.',
      );
    },
  });

  const error = start.error ?? confirm.error ?? preference.error;
  const busy = start.isPending || confirm.isPending || preference.isPending;

  return (
    <div className="account__phone">
      <p className="muted">
        A second factor at sign-in: an authenticator app, or a text message to your verified phone.
      </p>
      {done ? <Alert kind="success">{done}</Alert> : null}
      {error ? <Alert kind="error">{errorMessage(error)}</Alert> : null}

      {enrolment ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            confirm.mutate();
          }}
          noValidate
        >
          <p>
            Add this secret to your authenticator app, then enter the code it shows:{' '}
            <code>{enrolment.secret}</code>
          </p>
          <p>
            <a href={enrolment.otpauthUri}>Open in an authenticator app</a>
          </p>
          <div className="form form--inline">
            <TextField
              label="Authenticator code"
              name="totpCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button type="submit" loading={confirm.isPending} disabled={code.trim().length !== 6}>
              Confirm authenticator
            </Button>
          </div>
        </form>
      ) : (
        <div className="form form--inline">
          <Button
            type="button"
            variant="secondary"
            loading={start.isPending}
            disabled={busy}
            onClick={() => start.mutate()}
          >
            Set up an authenticator app
          </Button>
          {hasVerifiedPhone ? (
            <Button
              type="button"
              variant="secondary"
              loading={preference.isPending && preference.variables === 'SMS'}
              disabled={busy}
              onClick={() => preference.mutate('SMS')}
            >
              Use text message
            </Button>
          ) : null}
          {me.user.isMfaEnabled ? (
            <Button
              type="button"
              variant="secondary"
              loading={preference.isPending && preference.variables === 'NONE'}
              disabled={busy}
              onClick={() => preference.mutate('NONE')}
            >
              Turn off
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Prospects (accounts with no organization yet) receive a monthly promotional email unless they opt out. */
function MarketingPreference() {
  const me = useMe();
  const { refreshMe } = useAuth();
  const optedOut = me.user.marketingOptOutAt !== null;
  const toggle = useMutation({
    mutationFn: async () => {
      await authApi.setMarketingPreference(!optedOut);
      await refreshMe().catch(() => null);
    },
  });
  return (
    <Card title="Email preferences">
      <dl className="kv">
        <dt>Promotional email</dt>
        <dd>
          <Badge tone={optedOut ? 'neutral' : 'ok'}>{optedOut ? 'off' : 'on'}</Badge>{' '}
          <span className="muted">
            {optedOut
              ? 'You will not receive promotional email from Balsam Hub.'
              : 'Occasional news about Balsam Hub solutions. Sign-in codes and security notices are always sent.'}
          </span>
        </dd>
      </dl>
      {toggle.error ? <Alert kind="error">{errorMessage(toggle.error)}</Alert> : null}
      <div className="form form--inline">
        <Button
          type="button"
          variant="secondary"
          loading={toggle.isPending}
          onClick={() => toggle.mutate()}
        >
          {optedOut ? 'Turn promotional email on' : 'Turn promotional email off'}
        </Button>
      </div>
    </Card>
  );
}

function accountStateTone(state: AccountState): 'ok' | 'warn' | 'bad' {
  if (state === 'ACTIVE') return 'ok';
  if (state === 'UNVERIFIED' || state === 'LOCKED') return 'warn';
  return 'bad';
}

/**
 * Add or change the phone number and verify it with the code the provider texts.
 * A verified phone is what makes "Sign in with phone" available.
 */
function PhoneVerification() {
  const me = useMe();
  const { refreshMe } = useAuth();
  const [phone, setPhone] = useState(me.user.phone ?? '');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);

  const send = useMutation({
    mutationFn: async () => {
      setVerified(false);
      const value = phone.replace(/[\s()-]/g, '');
      if (!E164.test(value)) {
        setFieldError('Use international format, e.g. +14155552671.');
        return;
      }
      setFieldError(null);
      const changed = value !== me.user.phone;
      const delivery = await authApi.sendPhoneCode(changed ? value : undefined);
      if (changed) await refreshMe().catch(() => null);
      setCodeSent(true);
      return delivery;
    },
  });

  const verify = useMutation({
    mutationFn: async () => {
      await authApi.verifyPhone(code.trim());
      await refreshMe().catch(() => null);
      setCodeSent(false);
      setCode('');
      setVerified(true);
    },
  });

  const hasVerifiedPhone = Boolean(me.user.phone) && me.user.isPhoneVerified;

  return (
    <div className="account__phone">
      <p className="muted">
        A verified phone lets you sign in with a text-message code and receive SMS codes.
      </p>
      {verified ? <Alert kind="success">Your phone number is verified.</Alert> : null}
      {send.error ? <Alert kind="error">{errorMessage(send.error)}</Alert> : null}
      {verify.error ? <Alert kind="error">{errorMessage(verify.error)}</Alert> : null}

      <form
        className="form form--inline"
        onSubmit={(e) => {
          e.preventDefault();
          send.mutate();
        }}
        noValidate
      >
        <TextField
          label={hasVerifiedPhone ? 'New phone number' : 'Phone number'}
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+14155552671"
          value={phone}
          error={fieldError}
          onChange={(e) => setPhone(e.target.value)}
        />
        <Button type="submit" variant="secondary" loading={send.isPending} disabled={!phone}>
          {codeSent ? 'Resend code' : 'Send verification code'}
        </Button>
      </form>

      {codeSent ? (
        <form
          className="form form--inline"
          onSubmit={(e) => {
            e.preventDefault();
            verify.mutate();
          }}
          noValidate
        >
          <TextField
            label={`Code sent to ${send.data?.destination ?? 'your phone'}`}
            name="phoneCode"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={10}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <Button type="submit" loading={verify.isPending} disabled={code.trim().length < 4}>
            Verify phone
          </Button>
        </form>
      ) : null}
    </div>
  );
}
